/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * HOOK: useValidacaoImovel
 * ------------------------------------------------------------
 * Uso:
 *   const { status, pendencias, badge, conformidade, carregarBackend }
 *     = useValidacaoImovel(dossie, { imovelUuid, autoValidar });
 * ============================================================
 */

import { useMemo, useState, useCallback } from 'react';
import ValidadorImovelService from '../services/ValidadorImovelService';

export default function useValidacaoImovel(dossie, opts = {}) {
  const { imovelUuid = null, autoValidar = false } = opts;

  const [backendEstado, setBackendEstado] = useState(null);
  const [validando, setValidando] = useState(false);
  const [erro, setErro] = useState(null);

  /* ---------- Validação local (sincrona, instantânea) ---------- */

  const local = useMemo(() => {
    if (!dossie) {
      return {
        status: ValidadorImovelService.STATUS.NAO_CONFORME,
        conforme: false,
        pendencias: [],
        por_fase: {},
        total_pendencias: 0,
      };
    }
    return ValidadorImovelService.validarImovel(dossie);
  }, [dossie]);

  const badge = useMemo(
    () => ValidadorImovelService.badgeStatus(local.status),
    [local.status]
  );

  /* ---------- Validação no backend (persiste + cria tarefas) ---------- */

  const carregarBackend = useCallback(async () => {
    if (!imovelUuid) return null;
    setValidando(true);
    setErro(null);
    try {
      const resp = await fetch('/api/imovel/validarImovel.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imovel_uuid: imovelUuid, dossie }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) throw new Error(data.erro || 'Falha ao validar');
      setBackendEstado(data);
      return data;
    } catch (e) {
      setErro(e.message);
      return null;
    } finally {
      setValidando(false);
    }
  }, [imovelUuid, dossie]);

  /* ---------- Auto-exec (opcional) ---------- */

  if (autoValidar && imovelUuid && !backendEstado && !validando && !erro) {
    // Dispara uma vez no mount
    setTimeout(() => { carregarBackend(); }, 0);
  }

  return {
    // Resultado local (o que a UI mostra)
    status: local.status,
    conforme: local.conforme,
    pendencias: local.pendencias,
    por_fase: local.por_fase,
    total_pendencias: local.total_pendencias,
    badge,
    podeAvancar: true,           // ← decisão do usuário: sempre libera

    // Estado do backend
    validando,
    erro,
    backendEstado,

    // Ações
    carregarBackend,
  };
}